import { CellClassParams, IAggFuncParams, ICellRendererParams, IFilterDef, IFilterParams, ValueFormatterFunc, ValueFormatterParams } from 'ag-grid-community';
import dayjs from 'dayjs';
import { Currency, ExpiryStatus, PositionModel } from '../../../../../../Generated/Commodity-Management-Api';
import Formatting from '../../../../../../Helpers/Formatting';
import { GridRow, GroupingOption } from '../../../../../../Types';
import { StoneXNumber } from '../../../../../StoneXMui';
import { KnownPositionCustomData } from '../LegacyReports/Types';

// Position Report Types
export interface PositionReportConfiguration {
    inputs: {
        quoteDate?: {
            show: boolean;
        };
        budgetStartDate?: {
            show: boolean;
        };
        budgetEndDate?: {
            show: boolean;
        };
    };
    behavior?: {
        expiryStatus?: ExpiryStatus;
        netOptionPremiums?: boolean;
        grouping?: GroupingOption[];
    };
    columns: {
        available: PositionReportColumn[];
        active: PositionReportColumn[];
    };
};

export interface PositionReportUserOverride {
    behavior?: {
        grouping?: GroupingOption[];
    },
    columns?: {
        active?: PositionReportColumn[];
    };
}

export interface PositionReportRow extends GridRow, Partial<PositionModel>, KnownPositionCustomData { }

interface PositionColumnConfiguration {
    colId: string;
    headerName?: string; // This is what appears on the table header
    columnName?: string; // This is a unique column name to distinguish from other versions of the same column
    parentHeader?: string;
    field?: string;
    width?: number;
    maxWidth?: number;
    minWidth?: number;
    flex?: number;
    sortable?: boolean;
    hide?: boolean;
    filter?: string;
    cellClass?: string | string[] | ((params: CellClassParams<PositionReportRow, any>) => string | string[] | undefined);
    aggFunc?: string | ((params: IAggFuncParams<PositionReportRow, any>) => any) | null | undefined;
    valueFormatter?: (params: ValueFormatterParams<PositionReportRow>) => string | ValueFormatterFunc<PositionReportRow, any>;
    cellRenderer?: (params: ICellRendererParams<PositionReportRow>) => any;
    filterParams?: any;
}

export type PositionReportColumn = 'BudgetMonth' | 'TradeDate' | 'Commodity' | 'Counterparty' | 'Portfolio' | 'ContractMonth' | 'Side' | 'DealTicket' | 'Volume' | 'Currency' | 'TradePrice' | 'PositionType' | 'SettlementType' | 'SettlementDate' | 'PositionTypeOrOption' | 'OptionType' | 'OptionStyle' | 'OptionStrike' | 'ScenarioMarketPrice' | 'ScenarioMtmPrice' | 'ScenarioMtmValue' | 'MarketPrice' | 'MtmPrice' | 'MtmValue' | 'Trigger' | 'Decile' | 'Fee' | 'Actions';

export const PositionColumns: { [key in PositionReportColumn]: PositionColumnConfiguration } = {
    BudgetMonth: {
        colId: 'Budget Month',
        headerName: 'Budget Month',
        field: 'budgetDate.label',
        maxWidth: 150,
        filter: 'agDateColumnFilter',
        filterParams: {
            browserDatePicker: true,
            comparator: (filterDate: Date, cellValue: string) => {
                const cellDate = dayjs(cellValue).toDate();
                if (filterDate < cellDate) return 1;
                if (filterDate > cellDate) return -1;
                return 0;
            }
        }
    },
    Commodity: {
        colId: 'Commodity',
        headerName: 'Commodity',
        field: 'commodity.commodityName',
        filter: 'agSetColumnFilter',
        minWidth: 125,
        flex: 1,
        sortable: true,
    },
    TradeDate: {
        colId: 'Trade Date',
        headerName: 'Trade Date',
        field: 'tradeDate',
        valueFormatter: (params: ValueFormatterParams<PositionReportRow>) => params.node?.allChildrenCount ?? 0 > 0 ? '' : dayjs(params.value).format('L'),
        filter: 'agDateColumnFilter',
        filterParams: {
            browserDatePicker: true,
            comparator: (filterDate: Date, cellValue: string) => {
                const cellDate = dayjs(cellValue).toDate();
                if (filterDate < cellDate) return 1;
                if (filterDate > cellDate) return -1;
                return 0;
            }
        },
        minWidth: 100,
        flex: 1,
    },
    Counterparty: {
        colId: 'Counterparty',
        headerName: 'Counterparty',
        field: 'counterparty.counterpartyName',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 125,
        flex: 1,
    },
    Portfolio: {
        colId: 'Portfolio',
        headerName: 'Portfolio',
        field: 'portfolio.portfolioName',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 125,
        flex: 1,
    },
    ContractMonth: {
        colId: 'Contract Month',
        headerName: 'Contract Month',
        field: 'contractDate.label',
        filter: 'agDateColumnFilter',
        filterParams: {
            browserDatePicker: true,
            comparator: (filterDate: Date, cellValue: string) => {
                const cellDate = dayjs(cellValue).toDate();
                if (filterDate < cellDate) return 1;
                if (filterDate > cellDate) return -1;
                return 0;
            }
        },
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    Side: {
        colId: 'Side',
        headerName: 'Side',
        field: 'side',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    DealTicket: {
        colId: 'Deal Ticket',
        headerName: 'Deal Ticket',
        field: 'dealTicketNumber',
        filter: 'agTextColumnilter',
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    Volume: {
        colId: 'Volume',
        headerName: 'Volume',
        field: 'volume',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: ICellRendererParams<PositionReportRow>) => params.node.allChildrenCount ?? 0 > 0 ? null : <StoneXNumber number={params.value}></StoneXNumber>,
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    Currency: {
        colId: 'Currency',
        headerName: 'Currency',
        field: 'currency',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    TradePrice: {
        colId: 'Trade Price',
        headerName: 'Trade Price',
        field: 'price',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: ICellRendererParams<PositionReportRow>) => params.node.allChildrenCount ?? 0 > 0 ? null : <StoneXNumber number={params.value} currency={Currency.Usd} decimals={params.data?.commodity?.displayPrecision}></StoneXNumber>,
        minWidth: 100,
        flex: 1,
    },
    PositionType: {
        colId: 'Position Type',
        headerName: 'Position Type',
        field: 'positionType',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    SettlementType: {
        colId: 'Settlement Type',
        headerName: 'Settlement Type',
        columnName: 'Settlement Type',
        field: 'settlementSymbol',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    SettlementDate: {
        colId: 'Settlement Date',
        headerName: 'Settlement Date',
        columnName: 'Settlement Date',
        field: 'expirationDate',
        valueFormatter: (params: ValueFormatterParams<PositionReportRow>) => params.node?.allChildrenCount ?? 0 > 0 ? '' : dayjs(params.value).format('L'),
        filter: 'agDateColumnFilter',
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    PositionTypeOrOption: {
        colId: 'Position/Option Type',
        headerName: 'Position Type',
        columnName: 'Position/Option Type',
        field: 'positionTypeOrOption',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    OptionType: {
        colId: 'Option Type',
        headerName: 'Option Type',
        field: 'optionType',
        valueFormatter: (params: ValueFormatterParams<PositionReportRow>) => params.value ? params.value : '',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    OptionStyle: {
        colId: 'Option Style',
        headerName: 'Option Style',
        field: 'optionStyle',
        valueFormatter: (params: ValueFormatterParams<PositionReportRow>) => params.value ? params.value : '',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    OptionStrike: {
        colId: 'Option Strike',
        headerName: 'Option Strike',
        field: 'optionStrike',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: ICellRendererParams<PositionReportRow>) => params.node.allChildrenCount ?? 0 > 0 ? null : <StoneXNumber number={params.value} currency={Currency.Usd} decimals={params.data?.commodity?.displayPrecision}></StoneXNumber>,
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    ScenarioMarketPrice: {
        colId: 'Scenario Market Price',
        headerName: 'Scenario Market Price',
        field: 'scenarioMarketPrice',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: ICellRendererParams<PositionReportRow>) => params.node.allChildrenCount ?? 0 > 0 ? null : <StoneXNumber number={params.value} currency={Currency.Usd} decimals={params.data?.commodity?.displayPrecision}></StoneXNumber>,
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    ScenarioMtmPrice: {
        colId: 'Scenario Mtm Price',
        headerName: 'Scenario MTM Price',
        field: 'scenarioMarkToMarketPrice',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: ICellRendererParams<PositionReportRow>) => params.node.allChildrenCount ?? 0 > 0 ? null : <StoneXNumber number={params.value} currency={Currency.Usd} decimals={params.data?.commodity?.displayPrecision}></StoneXNumber>,
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    ScenarioMtmValue: {
        colId: 'Scenario Mtm Value',
        headerName: 'Scenario MTM Value',
        field: 'scenarioMarkToMarketValue',
        filter: 'agNumberColumnFilter',
        aggFunc: 'sum',
        cellClass: (params: CellClassParams<PositionReportRow, any>) => params.node.rowIndex == params.api.getDisplayedRowCount() - 1
            ? 'justify-right'
            : Formatting.getAgGridProfitLossCellClasses(params),
        cellRenderer: (params: ICellRendererParams<PositionReportRow>) => params.node.rowIndex == params.api.getDisplayedRowCount() - 1
            ? <h3><StoneXNumber number={params.value} currency={Currency.Usd} colored></StoneXNumber></h3>
            : <StoneXNumber number={params.value} currency={Currency.Usd}></StoneXNumber>,
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    MarketPrice: {
        colId: 'Market Price',
        headerName: 'Market Price',
        field: 'marketPrice',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: ICellRendererParams<PositionReportRow>) => params.node.allChildrenCount ?? 0 > 0 ? null : <StoneXNumber number={params.value} currency={Currency.Usd} decimals={params.data?.commodity?.displayPrecision}></StoneXNumber>,
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    MtmPrice: {
        colId: 'Mtm Price',
        headerName: 'MTM Price',
        field: 'markToMarketPrice',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: ICellRendererParams<PositionReportRow>) => params.node.allChildrenCount ?? 0 > 0 ? null : <StoneXNumber number={params.value} currency={Currency.Usd} decimals={params.data?.commodity?.displayPrecision}></StoneXNumber>,
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    MtmValue: {
        colId: 'Mtm Value',
        headerName: 'MTM Value',
        field: 'markToMarketValue',
        filter: 'agNumberColumnFilter',
        aggFunc: 'sum',
        cellClass: (params: CellClassParams<PositionReportRow, any>) => params.node.rowIndex == params.api.getDisplayedRowCount() - 1
            ? 'justify-right'
            : Formatting.getAgGridProfitLossCellClasses(params),
        cellRenderer: (params: ICellRendererParams<PositionReportRow>) => params.node.rowIndex == params.api.getDisplayedRowCount() - 1
            ? <h3><StoneXNumber number={params.value} currency={Currency.Usd} colored></StoneXNumber></h3>
            : <StoneXNumber number={params.value} currency={Currency.Usd}></StoneXNumber>,
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    Trigger: {
        colId: 'Trigger',
        headerName: 'Trigger',
        field: 'trigger',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    Decile: {
        colId: 'Decile',
        headerName: 'Decile',
        field: 'decile',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: ICellRendererParams<PositionReportRow>) => params.node.allChildrenCount ?? 0 > 0 ? null : <StoneXNumber number={params.value}></StoneXNumber>,
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    Fee: {
        colId: 'Fee',
        headerName: 'Fee',
        field: 'fee',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: ICellRendererParams<PositionReportRow>) => params.node.allChildrenCount ?? 0 > 0 ? null : <StoneXNumber number={params.value} decimals={3}></StoneXNumber>,
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    Actions: {
        colId: 'actions',
        headerName: '',
        width: 40,
        field: 'actions',
        filter: 'actionsFilter',
        //resizable: false,
        //cellStyle: { padding: '0' },
        sortable: false,
        //suppressAutoSize: true,
        //suppressMovable: true,
        
    }
};